import { FC, useCallback, useMemo } from 'react'
import styles from 'components/features/rewards/rewardPageCryptoWallet/WalletItem/WalletItem.styl'
import { CryptoCurrencyLogo } from 'components/features/rewards/rewardsPane/containers/CryptoCurrencyLogo/CryptoCurrencyLogo'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { getItemFromCollection } from 'helpers/normalizeHelpers'
import { ModifyFloatingPaneAction } from 'actions/floatingPanesActions'
import { FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE, FLOATING_PANE_CRYPTO_REWARDS_TYPE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT } from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { formatUSCurrency } from 'helpers/formatHelpers'
import cn from 'classnames'
import TextButton from 'components/atoms/buttons/TextButton'
import { CurrencyTicker } from 'components/features/rewards/rewardsPane/types/apiResponses'
import { createCryptoBalanceSelector } from 'components/features/rewards/rewardsPane/store/selectors/cryptoWallet'
import { CryptoWalletCurrency } from 'components/features/rewards/rewardsPane/types/cryptoWallet'
import { getBalanceUsd } from 'components/features/rewards/rewardPage/helpers'

type WalletItemProps = {
  ticker: CurrencyTicker
}
export const WalletItem: FC<WalletItemProps> = (props) => {

  const cryptoBalanceSelector = useMemo(createCryptoBalanceSelector, [])
  const item = useStoreSelector((state) => cryptoBalanceSelector(state, props.ticker))

  if (!item) {
    return null
  }

  return (
    <WalletItemInner item={ item } />
  )
}

type WalletItemInnerProps = {
  item: CryptoWalletCurrency
}
const WalletItemInner: FC<WalletItemInnerProps> = (props) => {
  const { item } = props
  const {
    id,
    performanceLas24HoursChangePercent, performanceLas24HoursChangeUSD,
    performanceAllTimePercent, performanceAllTimeChangeUSD,
    price, balance_crypto,
  } = item

  const name = useStoreSelector((state) => {
    const { currenciesMeta: meta } = state.data.cryptoRewardsPane
    if (!meta.currenciesMeta) {
      return null
    }

    return getItemFromCollection(meta.currenciesMeta, id)?.name
  })

  const amountUSD = useMemo(() => {
    return getBalanceUsd(balance_crypto, price)
  }, [balance_crypto, price])

  const dispatch = useStoreDispatch()
  const handleSell = useCallback(() => {
    dispatch(new ModifyFloatingPaneAction({
      type: FLOATING_PANE_CRYPTO_REWARDS_TYPE,
      mode: FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE,
      step: CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT,
      progressPercents: 20,
      fields: {
        cryptoCurrencyIDChosenForSelling: id,
      },
    }))
  }, [dispatch, id])

  const balanceChild = useMemo(() => {
    if (amountUSD === null) {
      return null
    }

    const displayBalance = formatUSCurrency(amountUSD, 'penny')
    if (amountUSD === '0.00') {
      return `< ${ formatUSCurrency(0.01, 'penny') }`
    }

    return displayBalance
  }, [amountUSD])

  const isSellAvailable = useMemo(() => {
    return amountUSD !== '0.00'
  }, [amountUSD])

  return (
    <div className={ styles.walletItem }>
      <div className={ styles.name }>
        <CryptoCurrencyLogo currencyId={ id } className={ styles.icon } />
        <div>
          { name }
        </div>
      </div>
      <div className={ styles.amount }>
        { balanceChild }
      </div>
      <div className={ styles.performanceDaily }>
        <PerformanceValue
          label='24 hours'
          usd={ performanceLas24HoursChangeUSD }
          percent={ performanceLas24HoursChangePercent }
        />
      </div>
      <div className={ styles.performanceAllTime }>
        <PerformanceValue
          label='All time'
          usd={ performanceAllTimeChangeUSD }
          percent={ performanceAllTimePercent }
        />
      </div>
      <div className={ cn(styles.action, !isSellAvailable && styles.disableSell) }>
        <TextButton onClick={ handleSell } size='regular'>
          SELL
        </TextButton>
      </div>
    </div>
  )
}

type PerformanceValueProps = {
  label: string
  usd: number | null
  percent: number | null
}
const PerformanceValue: FC<PerformanceValueProps> = (props) => {
  const { label, usd, percent } = props

  if (usd === null) {
    return null
  }
  return (
    <>
      <div className={ styles.label }>
        { label }
      </div>
      <div className={ cn(styles.value, usd < 0 && styles.negative, usd > 0 && styles.positive) }>
        { usd > 0 ? '+' : usd < 0 ? '-' : '' }
        { formatUSCurrency(Math.abs(usd), 'penny') }
        { usd === 0 || percent === null ? null : (
          <>
            { ' ' }
            ({ percent.toFixed(2) }%)
          </>
        )}
      </div>
    </>
  )
}
